import React, { useState, useEffect } from 'react';
import { Table, Input, Card, Typography, message, Button, Modal, Form, Input as AntInput, Select } from 'antd';
import { EditOutlined } from '@ant-design/icons'; // Importamos el ícono de lápiz
import { getUsuarios, updateUsuario, createUsuario } from '../api/api';  // Importamos las funciones getUsuarios, updateUsuario y createUsuario

const { Title } = Typography;
const { Option } = Select;

const UsuariosList = () => {

  //const navigate = useNavigate();
  //const logout = useLogout();

  const [usuarios, setUsuarios] = useState([]); // Inicializamos como un array vacío
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false); // Estado para saber si estamos en modo edición
  const [isCreating, setIsCreating] = useState(false); // Estado para saber si estamos en modo creación
  const [usuarioId, setUsuarioId] = useState(null); // Usuario que se está editando
  const [nombre, setNombre] = useState('');
  const [cedula, setCedula] = useState('');
  const [clave, setClave] = useState('');
  const [tipoUsuario, setTipoUsuario] = useState(1); // 1: Estudiante, 2: Docente

  // Fetch usuarios cuando el componente se monta
  useEffect(() => {
    const fetchUsuarios = async () => {
      const {success, status, message: errorMessage, usuarios} = await getUsuarios();  // Llamamos a la función getUsuarios de api.js
      if (success) {
        setUsuarios(usuarios); // Aseguramos que se asignen un array vacío si no hay usuarios
      }else{
        if(status === 401){
          console.log(errorMessage);
        }else{
          
        }
      }
      setLoading(false);
    };

    fetchUsuarios();
  },[]);

  const columnas = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Cédula',
      dataIndex: 'cedula',
      key: 'cedula',
    },
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      render: () => '****', // Siempre muestra '****' en la columna de clave
    },
    {
      title: 'Tipo de Rol',
      dataIndex: 'tipo_usuario',
      key: 'tipo_usuario',
      render: (tipo_usuario) => tipo_usuario?.nombre || 'Desconocido',
    },
    {
      title: 'Editar',
      key: 'editar',
      render: (_, usuario) => (
        <Button 
          icon={<EditOutlined />} 
          onClick={() => handleEdit(usuario)} 
          type="primary" 
        />
      ),
    },
  ];

  // Filtrar usuarios según el nombre
  const filtrarUsuarios = (text) => {
    setSearchText(text);
  };

  // Aseguramos que 'usuarios' sea un array y filtrar por nombre
  const usuariosFiltrados = (usuarios || []).filter((usuario) =>
    usuario.nombre.toLowerCase().includes(searchText.toLowerCase())
  );

  // Función para manejar la edición
  const handleEdit = (usuario) => {
    setIsEditing(true); // Activamos el modo edición
    setIsCreating(false); // Desactivamos el modo creación
    setUsuarioId(usuario.id);
    setNombre(usuario.nombre);
    setCedula(usuario.cedula);
    setTipoUsuario(usuario.tipo_usuario_id);
    setClave('');  // No mostramos la clave por defecto en el formulario
  };

  // Función para manejar la creación de un nuevo usuario
  const handleCreate = () => {
    setIsCreating(true); // Activamos el modo creación
    setIsEditing(false); // Desactivamos el modo edición
    setUsuarioId(null);
    setNombre('');
    setCedula('');
    setClave('');
    setTipoUsuario(1); // Valor por defecto para el rol
  };

  // Función para manejar la cancelación de la edición o creación
  const handleCancel = () => {
    setIsEditing(false); // Desactivamos el modo edición
    setIsCreating(false); // Desactivamos el modo creación
    setNombre('');
    setCedula('');
    setClave('');
    setTipoUsuario(1);
  };

  // Función para manejar el envío del formulario
  const handleSubmit = async () => {
    const usuarioData = { nombre, cedula, clave, tipo_usuario_id: tipoUsuario };

    try {
      let response;
      if (usuarioId) {
        // Si tenemos un usuarioId, es una edición
        response = await updateUsuario(usuarioId, nombre, cedula, clave, tipoUsuario);
      } else {
        // Si no tenemos usuarioId, es una creación
        response = await createUsuario(usuarioData);  // Llamamos a la función para crear un usuario
      }

      if (response.success) {
        message.success(usuarioId ? 'Usuario actualizado correctamente' : 'Usuario creado correctamente');
        setIsEditing(false);
        setIsCreating(false);
        setNombre('');
        setCedula('');
        setClave('');
        setTipoUsuario(1);
        // Recargamos los usuarios para reflejar los cambios
        const result = await getUsuarios();
        setUsuarios(result.usuarios);
      } else {
        Modal.info({ title: 'Error', content: response.message || 'Hubo un error' });
      }
    } catch (error) {
      console.error('Error al guardar usuario', error);
      message.error('Hubo un error al guardar el usuario');
    }
  };

  return (
    <Card style={{ maxWidth: 1000, margin: 'auto' }}>
      <Title level={4}>Lista de Usuarios</Title>
      <Input
        placeholder="Filtrar por nombre"
        value={searchText}
        onChange={(e) => filtrarUsuarios(e.target.value)}
        style={{ marginBottom: 20 }}
      />
      <Table
        dataSource={usuariosFiltrados}
        columns={columnas}
        pagination={{ pageSize: 5 }}
        bordered
        rowKey="id"
        loading={loading}
      />

      <Button type="primary" onClick={handleCreate} style={{ marginBottom: 20 }}>
        Crear Nuevo Usuario
      </Button>

      {/* Modal de edición y creación */}
      {(isEditing || isCreating) && (
        <Modal
          title={isEditing ? "Editar Usuario" : "Crear Usuario"}
          visible={isEditing || isCreating}
          onOk={handleSubmit}
          onCancel={handleCancel}
          okText={isEditing ? "Guardar" : "Crear"}
          cancelText="Cancelar"
        >
          <Form layout="vertical">
            <Form.Item label="Nombre">
              <AntInput
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre del usuario"
                required
              />
            </Form.Item>

            <Form.Item label="Cedula">
              <AntInput
                type="email"
                value={cedula}
                onChange={(e) => setCedula(e.target.value)}
                placeholder="Cédula"
                required
              />
            </Form.Item>

            <Form.Item label="Contraseña">
              <AntInput.Password
                value={clave}
                onChange={(e) => setClave(e.target.value)}
                placeholder="Contraseña"
              />
            </Form.Item>

            <Form.Item label="Rol">
              <Select
                value={tipoUsuario}
                onChange={(value) => setTipoUsuario(value)}
                required
              >
                <Option value={1}>DOCENTE</Option>
                <Option value={2}>ESTUDIANTE</Option>
                <Option value={3}>RECTOR</Option>
              </Select>
            </Form.Item>
          </Form>
        </Modal>
      )}
    </Card>
  );
};

export default UsuariosList;
